import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppState } from 'store/index';
import { isProtectedRoute, isKeepCachedOrderRoute, isPurchaseFlowTokenRoute, isEncodedRoute } from 'utils/Navigation';
import { encodeState, decodeState, cleanState } from 'utils/Navigation';
import { usePersistentStorage } from './usePersistentStorage';
import { useUpdateState } from './useUpdateState';
import { Constants } from 'utils/Constants';
//import { useAuthentication } from 'hooks/useAuthentication';

export const useStateNavigation = () => {
  const stations = useSelector((state: AppState) => state.locations.stopPlaces);
  const order = useSelector((state: AppState) => state.order);
  const user = useSelector((state: AppState) => state.user);
  const { setOrderState } = useUpdateState();
  const { cacheItem, getCachedItem, removeCachedItem } = usePersistentStorage();
  const reactNavigate = useNavigate();
  const [searchParams] = useSearchParams();

  const cacheRedirect = (route: string): void => cacheItem(Constants.CACHE_REDIRECT, route);
  const getCachedRedirect = (): string => getCachedItem(Constants.CACHE_REDIRECT);
  const removeRedirectFromCache = (): void => removeCachedItem(Constants.CACHE_REDIRECT);

  const getParams = () => {
    const params: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  };

  const navigateWithParams = (route: string, params: Record<string, unknown>) => {
    const queryString = new URLSearchParams(params as Record<string, string>).toString();
    const destination = queryString ? `${route}?${queryString}` : route;
    reactNavigate(destination);
  };

  const decodeJourneyState = () => {
    const state = decodeState(window.location, stations);
    if (!state.from) return false;
    const cleaned = Object.assign(order, state);
    setOrderState(cleaned);
    return true;
  };

  const handleNavigation = (route: string, fromPreloader: boolean) => {
    const state = decodeState(window.location, stations);
    const cleaned = fromPreloader ? Object.assign(order, state) : cleanState(route, Object.assign(order));
    setOrderState(cleaned);
    if (isEncodedRoute(route)) {
      const queryString = new URLSearchParams({ state: encodeState(cleaned) }).toString();
      reactNavigate(`${route}?${queryString}`);
    } else {
      //Detecting back button click
      window.onpopstate = () => {
        if (window.location.pathname === Constants.ROUTE_JOURNEY) {
          window.location.reload();
        } else {
          navigateWithState(window.location.pathname, false);
        }
      };

      //Prevent safari mem-cache
      window.onpageshow = (e: PageTransitionEvent): void => {
        if (e.persisted) window.location.reload();
      };
      reactNavigate(route);
    }
  };

  const cleanCachedData = (route: string) => {
    if (route === Constants.ROUTE_BASE) removeRedirectFromCache();
    if (!isKeepCachedOrderRoute(route)) removeCachedItem(Constants.CACHE_ORDER);
  };

  const navigateWithState = (route: string, fromPreloader = null): void => {
    if (isProtectedRoute(route) && !user?.account) {
      return reactNavigate(Constants.ROUTE_BASE);
    }
    if (fromPreloader && route === Constants.ROUTE_RETURN_JOURNEY) {
      return reactNavigate(Constants.ROUTE_JOURNEY);
    }
    if (fromPreloader && isPurchaseFlowTokenRoute(route) && !order.orderID) {
      return reactNavigate(Constants.ROUTE_BASE);
    }

    cleanCachedData(route);
    return handleNavigation(route, fromPreloader);
  };

  return {
    decodeJourneyState,
    navigateWithParams,
    getParams,
    navigateWithState,
    cacheRedirect,
    getCachedRedirect,
    removeRedirectFromCache,
  };
};
